import React, { useState } from "react";
import {
  MapPin,
  Edit2,
  Star,
  Trash,
  Plus,
  Home,
  Building2,
  MapPinned,
} from "lucide-react";
import type { AddressManagerProps } from "./types";
import ConfirmModal from "../_ResponsiveComponents/Modal/ConfirmModal";

const AddressManager: React.FC<AddressManagerProps> = ({
  addresses,
  addressTypes,
  selectedAddress,
  selectedAddressType,
  hasAddresses,
  addressesLoading,
  onSelectAddress,
  onSelectAddressType,
  onShowAddressModal,
  onShowAddAddressModal,
  onDeleteAddress,
  onSetDefaultAddress,
  onEditAddress,
}) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  if (addressesLoading) {
    return (
      <div className="bg-neutral-900 rounded-2xl shadow-xl border border-neutral-800 p-6 mb-6">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-white">
          <MapPin className="w-6 h-6 text-primary" />
          Shipping Address
        </h2>
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!hasAddresses) {
    return (
      <div className="bg-neutral-900 rounded-2xl shadow-xl border border-neutral-800 overflow-hidden mb-6">
        <div className="p-6 border-b border-neutral-800">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-white">
            <MapPin className="w-6 h-6 text-primary" />
            Shipping Address
          </h2>
        </div>
        <div className="p-8">
          <div className="border-2 border-dashed border-neutral-700 bg-neutral-800/50 rounded-2xl p-8 text-center">
            <div className="relative inline-block mb-4">
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl opacity-50"></div>
              <MapPin className="relative w-16 h-16 text-neutral-500 mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              No addresses found
            </h3>
            <p className="text-neutral-400 mb-6 max-w-sm mx-auto">
              Add your first shipping address to continue with checkout
            </p>
            <button
              onClick={onShowAddAddressModal}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-black px-8 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
            >
              <Plus className="w-5 h-5" />
              Add Address
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getAddressTypeIcon = (typeName: string) => {
    const lowerName = typeName.toLowerCase();
    if (lowerName.includes("home")) return <Home className="w-4 h-4" />;
    if (lowerName.includes("work") || lowerName.includes("office"))
      return <Building2 className="w-4 h-4" />;
    return <MapPinned className="w-4 h-4" />;
  };

  return (
    <div className="bg-neutral-900 rounded-2xl shadow-xl border border-neutral-800 overflow-hidden mb-6">
      {/* Header Section */}
      <div className="p-6 border-b border-neutral-800">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-white">
            <MapPin className="w-6 h-6 text-primary" />
            Shipping Address
          </h2>
          <button
            onClick={onShowAddAddressModal}
            className="flex items-center gap-2 bg-neutral-800 hover:bg-neutral-700 text-primary px-4 py-2 rounded-xl font-semibold border border-neutral-700 hover:border-primary/30 transition-all shadow-sm hover:shadow-md"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add New</span>
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Address Type Tabs */}
        <div
          className="flex items-center gap-2 mb-6 border-b border-neutral-800 overflow-x-auto pb-1"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#e54965 transparent",
          }}
        >
          {addressTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => {
                onSelectAddressType(type.id);
                const addressForType = addresses.find(
                  (addr) =>
                    addr.address_type_id === type.id ||
                    addr.address_type_id?.toString() === type.id.toString(),
                );
                if (addressForType) {
                  onSelectAddress(addressForType);
                }
              }}
              className={`relative px-5 py-3 font-semibold transition-all duration-300 rounded-t-xl whitespace-nowrap ${
                selectedAddressType === type.id ||
                selectedAddressType?.toString() === type.id.toString()
                  ? "text-primary bg-primary/10 border-b-4 border-primary shadow-sm"
                  : "text-neutral-400 hover:text-primary hover:bg-neutral-800"
              }`}
            >
              <span className="flex items-center gap-2">
                {getAddressTypeIcon(type.name)}
                {type.name}
              </span>
            </button>
          ))}
        </div>

        {/* Selected Address Display */}
        {selectedAddress && selectedAddressType && (
          <div className="relative group">
            <div className="absolute -inset-1 bg-primary/20 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
            <div
              className="relative border border-primary/30 rounded-2xl p-6 cursor-pointer hover:border-primary/50 transition-all duration-200 bg-neutral-900 shadow-lg hover:shadow-xl"
              onClick={onShowAddressModal}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-md">
                      <MapPin className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">
                        {selectedAddress.address_type_value || "Address"}
                      </h3>
                      {selectedAddress.is_default && (
                        <div className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded-full mt-1">
                          <Star className="w-3 h-3 fill-current" />
                          <span className="font-semibold">Default Address</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-neutral-300 ml-15">
                    <div className="font-semibold text-base">
                      {selectedAddress.street}
                    </div>
                    {[
                      selectedAddress.building &&
                        `Building ${selectedAddress.building}`,
                      selectedAddress.floor && `Floor ${selectedAddress.floor}`,
                      selectedAddress.apartment &&
                        `Apt ${selectedAddress.apartment}`,
                    ].filter(Boolean).length > 0 && (
                      <div className="text-sm text-neutral-400">
                        {[
                          selectedAddress.building &&
                            `Building ${selectedAddress.building}`,
                          selectedAddress.floor &&
                            `Floor ${selectedAddress.floor}`,
                          selectedAddress.apartment &&
                            `Apt ${selectedAddress.apartment}`,
                        ]
                          .filter(Boolean)
                          .join(" • ")}
                      </div>
                    )}
                    <div className="text-sm font-medium text-white">
                      {selectedAddress.city}
                    </div>
                    {selectedAddress.description && (
                      <div className="text-sm text-neutral-400 italic mt-2 pt-2 border-t border-neutral-800">
                        {selectedAddress.description}
                      </div>
                    )}
                  </div>
                </div>

                {/* Action buttons: desktop (inline) and mobile (3-dots dropdown) */}
                <div className="ml-4 flex items-center gap-2">
                  {/* Desktop actions */}
                  <div className="hidden md:flex flex-col gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onEditAddress && selectedAddress) {
                          onEditAddress(selectedAddress);
                        } else {
                          onShowAddressModal();
                        }
                      }}
                      className="p-2.5 rounded-xl bg-neutral-800 border border-neutral-700 text-primary hover:bg-neutral-700 hover:border-primary/30 transition-all duration-200 group/btn shadow-sm hover:shadow-md"
                      title="Edit address"
                    >
                      <Edit2 className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteOpen(true);
                      }}
                      className="p-2.5 rounded-xl bg-neutral-800 border border-neutral-700 text-red-400 hover:bg-red-500/10 hover:border-red-500/30 transition-all duration-200 group/btn shadow-sm hover:shadow-md"
                      title="Delete address"
                    >
                      <Trash className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                    </button>
                    {!selectedAddress.is_default && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSetDefaultAddress(selectedAddress.id);
                        }}
                        className="p-2.5 rounded-xl bg-neutral-800 border border-neutral-700 text-amber-400 hover:bg-amber-500/10 hover:border-amber-500/30 transition-all duration-200 group/btn shadow-sm hover:shadow-md"
                        title="Set as default"
                      >
                        <Star className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                      </button>
                    )}
                  </div>

                  {/* Mobile: 3-dots dropdown */}
                  <details
                    className="relative md:hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <summary
                      className="p-3 rounded-xl bg-neutral-800 border border-neutral-700 text-primary hover:bg-neutral-700 hover:border-primary/30 transition-all duration-200 cursor-pointer flex items-center justify-center shadow-sm"
                      aria-label="More options"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg
                        className="w-5 h-5"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="5" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="12" cy="19" r="1.5" />
                      </svg>
                    </summary>

                    <div className="absolute right-0 mt-2 w-48 bg-neutral-800 border border-neutral-700 rounded-xl shadow-xl z-50 overflow-hidden">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onEditAddress && selectedAddress) {
                            onEditAddress(selectedAddress);
                          } else {
                            onShowAddressModal();
                          }
                          const d = e.currentTarget.closest(
                            "details",
                          ) as HTMLDetailsElement | null;
                          if (d) d.open = false;
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-neutral-700 flex items-center gap-3 transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-neutral-200">
                          Edit
                        </span>
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDeleteOpen(true);
                          const d = e.currentTarget.closest(
                            "details",
                          ) as HTMLDetailsElement | null;
                          if (d) d.open = false;
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-red-500/10 flex items-center gap-3 transition-colors"
                      >
                        <Trash className="w-4 h-4 text-red-400" />
                        <span className="text-sm font-medium text-red-400">
                          Delete
                        </span>
                      </button>

                      {!selectedAddress.is_default && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onSetDefaultAddress(selectedAddress.id);
                            const d = e.currentTarget.closest(
                              "details",
                            ) as HTMLDetailsElement | null;
                            if (d) d.open = false;
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-amber-500/10 flex items-center gap-3 transition-colors"
                        >
                          <Star className="w-4 h-4 text-amber-400" />
                          <span className="text-sm font-medium text-neutral-200">
                            Set as default
                          </span>
                        </button>
                      )}
                    </div>
                  </details>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show message if no address for selected type */}
        {selectedAddressType &&
          !addresses.find(
            (addr) =>
              addr.address_type_id === selectedAddressType ||
              addr.address_type_id?.toString() ===
                selectedAddressType?.toString(),
          ) && (
            <div className="border-2 border-dashed border-neutral-700 rounded-2xl p-8 text-center bg-neutral-800/50">
              <div className="relative inline-block mb-3">
                <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl opacity-30"></div>
                <MapPin className="relative w-12 h-12 text-neutral-500 mx-auto" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                No Address Found
              </h3>
              <p className="text-neutral-400 mb-5 max-w-xs mx-auto">
                Add an address for this type to continue with your order
              </p>
              <button
                onClick={() => {
                  const selectedType = addressTypes.find(
                    (type) =>
                      type.id === selectedAddressType ||
                      type.id.toString() === selectedAddressType?.toString(),
                  );
                  if (selectedType) {
                    onShowAddAddressModal();
                  }
                }}
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-black px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
              >
                <Plus className="w-5 h-5" />
                Add Address for this Type
              </button>
            </div>
          )}

        {/* Delete confirmation modal */}
        <ConfirmModal
          isOpen={confirmDeleteOpen}
          title="Delete address"
          message="Are you sure you want to delete this address? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onConfirm={() => {
            if (selectedAddress) {
              onDeleteAddress(selectedAddress.id);
            }
            setConfirmDeleteOpen(false);
          }}
          onCancel={() => setConfirmDeleteOpen(false)}
        />
      </div>
    </div>
  );
};

export default AddressManager;
